import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Editor from '@components/common/editor/Editor';
import InputErrorMessage from '@components/common/Formik/InputErrorMessage/InputErrorMessage';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Modal from '@components/common/Modals/Modal/Modal';
import Text from '@components/common/Text/Text';
import greetingCardService from '@services/domain/Lpc/GreetingCardService';
import { addMessageSchema } from '@validations/potSchema';

import css from './AddMessageForm.module.scss';

type AddMessageFormProps = {
    isVisible: boolean;
    onHide: () => void;
    potRef: string;
    message?: string;
    potCategory: string;
    onGetGreetingCardMessages: () => void;
    orderId: string;
};

const AddMessageForm = ({
    isVisible,
    onHide,
    potRef,
    message,
    potCategory,
    orderId,
    onGetGreetingCardMessages,
}: AddMessageFormProps) => {
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const { addToast } = useToasts();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const onSubmit = (values) => {
        setIsLoading(true);
        greetingCardService
            .addMessage(potRef, values.message, orderId)
            .then(() => onHide())
            .then(() => {
                onGetGreetingCardMessages && onGetGreetingCardMessages();
                addToast(t(`success.MESSAGE_SENT`), {
                    appearance: 'success',
                    autoDismiss: true,
                });
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <Modal visible={isVisible} onHide={onHide} customClass={css.custom}>
            <div className={css.header}>
                <h5>{t('greeting-card.form.title')}</h5>
                <Text variant={'body_02'} color={'txt-secondary'}>
                    {potCategory !== 'BEREAVEMENT' ? t('greeting-card.form.desc') : t('greeting-card.form.death-desc')}
                </Text>
            </div>

            <Formik
                initialValues={{ message: message || '' }}
                validationSchema={addMessageSchema}
                onSubmit={(values) => onSubmit(values)}
            >
                {({ values, errors }) => (
                    <Form className={css.form}>
                        <div className={css.form__content}>
                            <div className={css.form__input}>
                                <Editor
                                    value={values.message}
                                    editorName="message"
                                    editorImageUploadUrl={'wishcard'}
                                    editorPlaceholder={
                                        potCategory !== 'BEREAVEMENT'
                                            ? t('greeting-card.form.message-placeholder')
                                            : t('greeting-card.form.death-message-placeholder')
                                    }
                                    editorId="wishcard-message"
                                />
                                {errors.message && isSubmitting && (
                                    <InputErrorMessage
                                        customClass={css.input__errors}
                                        messageCode={values.message ? 'MAX_LENGTH' : 'REQUIRED'}
                                        name={'message_wishcard'}
                                    />
                                )}
                            </div>

                            <div className={css.form__submit}>
                                <Button
                                    type={'button'}
                                    customClass={css.form__submit__cta}
                                    variant={'secondary'}
                                    onClick={() => {
                                        onHide();
                                    }}
                                >
                                    {t('greeting-card.form.cancel')}
                                </Button>
                                <Button
                                    onClick={() => setIsSubmitting(true)}
                                    type={'submit'}
                                    variant={'primary'}
                                    customClass={css.form__submit__cta}
                                    isLoading={isLoading}
                                    isDisabled={isLoading}
                                >
                                    {t('greeting-card.form.success')}
                                </Button>
                            </div>
                        </div>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default AddMessageForm;
